import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { VERSION } from './version';

// Components
import HomePage from './components/HomePage';
import ArticleModal from './components/ArticleModal';
import ArticleDirectLink from './components/ArticleDirectLink';
import ShareButton from './components/ShareButton';
import AutoUpdateManager from './components/AutoUpdateManager';
import FilterButton from './components/FilterButton';
import SlideMenu from './components/SlideMenu';

// Utils
import { 
  getTheme, 
  saveTheme, 
  initializeTheme,
  getSelectedRegions,
  getSelectedCategories,
  migrateLocalStorageKeys,
  trackEvent,
  trackError
} from './utils/helpers';


// Header component with navigation
const Header = ({ theme, toggleTheme, onFilterClick, hasActiveFilters, onTitleClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isArticlePage = location.pathname.startsWith('/artikel/');

  const handleBackClick = () => {
    trackEvent('header-back-clicked', { 
      from: location.pathname,
      hasHistory: window.history.length > 1
    });
    
    // Check if there's browser history to go back to
    // history.length > 1 means there's at least one previous page
    if (window.history.length > 1) {
      // Try to go back in browser history
      window.history.back();
      
      // Fallback: If going back doesn't work after a short delay
      // (e.g., if we go back to an external site), navigate to homepage
      setTimeout(() => {
        // Check if we're still on an article page after going back
        if (window.location.pathname.startsWith('/artikel/')) {
          navigate('/', { replace: true });
        }
      }, 100);
    } else {
      // No history available (direct visit), navigate to homepage
      navigate('/', { replace: true });
    }
  };

  const handleTitleClick = (e) => {
    // Prevent default link behavior
    e.preventDefault();
    
    // Only navigate if we're not already on the homepage
    if (location.pathname !== '/') {
      trackEvent('header-title-clicked', { 
        from: location.pathname 
      });
      navigate('/', { replace: false });
    } else {
      // Already on homepage - scroll to top and refresh
      trackEvent('header-title-clicked-on-homepage');
      // Scroll to top first (priority 1)
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Then trigger refresh if callback is provided (priority 2)
      if (onTitleClick) {
        onTitleClick();
      }
    }
  };

  return (
    <header className="header">
      <div className="header-content">
        {isArticlePage && (
          <button onClick={handleBackClick} className="header-back-button" aria-label="Tillbaka">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 16L6 10L12 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        {!isArticlePage && (
          <FilterButton 
            onClick={onFilterClick}
            hasActiveFilters={hasActiveFilters}
          />
        )}
        <a href="/" className="site-title" onClick={handleTitleClick}>
          Spanska Nyheter
        </a>
        <div className="header-actions">
          {isArticlePage && <ShareButton />}
          {!isArticlePage && 
            <button 
              className="theme-toggle" 
              onClick={toggleTheme}
              aria-label="Växla tema"
            >
              <div className={`theme-toggle-circle ${theme === 'dark' ? 'moon' : 'sun'}`}></div>
            </button>
          }
        </div>
      </div>
    </header>
  );
};

// Main App component - Using traditional Router syntax
function App() {
  const [theme, setThemeState] = useState(getTheme());
  const [isSlideMenuOpen, setIsSlideMenuOpen] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState(getSelectedRegions());
  const [selectedCategories, setSelectedCategories] = useState(getSelectedCategories());
  const [allCategories, setAllCategories] = useState([]);
  const [totalRegionsCount, setTotalRegionsCount] = useState(22); // Default to 22 regions
  
  // Modal state
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isDirectVisit, setIsDirectVisit] = useState(false);
  
  // Ref to hold refresh function from HomePage
  const homePageRefreshRef = useRef(null);

  // Initialize theme and migrate localStorage on mount
  useEffect(() => {
    try {
      migrateLocalStorageKeys();  // Migrate old localStorage keys to new structure
      initializeTheme();
      trackEvent('app-loaded', { 
        version: VERSION,
        theme: getTheme()
      });
    } catch (error) {
      trackError('app-init-error', error, { 
        stage: 'initialization' 
      });
    }
  }, []);

  // Load categories early to prevent double-loading in HomePage
  // This ensures excludedCategories is calculated correctly from the start
  useEffect(() => {
    const loadInitialCategories = async () => {
      try {
        const { fetchCategories } = await import('./utils/api');
        const categoriesData = await fetchCategories();
        if (categoriesData && categoriesData.length > 0) {
          setAllCategories(categoriesData);
        }
      } catch (error) {
        // Silent fail - SlideMenu will load categories as fallback
        console.warn('Could not preload categories:', error);
      }
    };
    
    loadInitialCategories();
  }, []);

  // Handle browser back button for modal
  useEffect(() => {
    const handlePopState = (event) => {
      if (isArticleModalOpen) {
        setIsArticleModalOpen(false);
        setSelectedArticle(null);
        setIsDirectVisit(false); // Reset direct visit flag
        // Restore the homepage URL
        window.history.replaceState(null, '', '/');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isArticleModalOpen]);

  // Handle article selection - memoized to prevent unnecessary re-renders
  const handleArticleClick = useCallback((article, isDirect = false) => {
    setSelectedArticle(article);
    setIsArticleModalOpen(true);
    setIsDirectVisit(isDirect);
  }, []);

  // Handle modal close - let the modal handle its own animation
  const handleModalClose = useCallback(() => {
    setIsArticleModalOpen(false);
    setSelectedArticle(null);
    setIsDirectVisit(false); // Reset direct visit flag
  }, []);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    trackEvent('theme-changed', { 
      from: theme, 
      to: newTheme 
    });
    setThemeState(newTheme);
    saveTheme(newTheme);
  }, [theme]);

  // Handle region change
  const handleRegionsChange = (newRegions) => {
    setSelectedRegions(newRegions);
  };

  // Handle category change (receives excluded categories)
  const handleCategoriesChange = (excludedCategories) => {
    // Store selected categories (inverse of excluded)
    const selected = allCategories.filter(c => !excludedCategories.includes(c));
    setSelectedCategories(selected);
  };

  // Check if any filters are active - memoized to prevent repeated calculations
  const hasActiveFilters = useMemo(() => {
    // When no stored selection exists (length 0), we treat it as "all selected"
    // So only consider it as filtered if some but not all are selected
    const hasRegionFilter = selectedRegions.length > 0 && selectedRegions.length < totalRegionsCount;
    
    // For categories, handle the case when allCategories hasn't been loaded yet
    let hasCategoryFilter = false;
    if (allCategories.length > 0) {
      // Categories have been loaded from API
      hasCategoryFilter = selectedCategories.length > 0 && selectedCategories.length < allCategories.length;
    } else if (selectedCategories.length > 0) {
      // Categories not loaded yet, but we have saved selections
      // Assume it's filtered since user has made selections before
      hasCategoryFilter = true;
    }
    
    const result = hasRegionFilter || hasCategoryFilter;
    
    return result;
  }, [selectedRegions.length, totalRegionsCount, selectedCategories.length, allCategories.length]);

  // Handle filter button click
  const handleFilterClick = useCallback(() => {
    trackEvent('filters-toggle', { 
      action: 'open',
      hasActiveFilters 
    });
    setIsSlideMenuOpen(true);
  }, [hasActiveFilters]);

  // Handle title click - triggers refresh on HomePage
  const handleTitleClick = useCallback(() => {
    if (homePageRefreshRef.current) {
      homePageRefreshRef.current();
    }
  }, []);

  // Calculate excluded categories for API - memoized to prevent infinite re-renders
  // Returns null when categories haven't loaded yet to prevent false empty array
  const excludedCategories = useMemo(() => {
    // Categories not loaded yet - return empty array to not filter anything
    if (allCategories.length === 0) return [];
    // If no categories are selected in state, treat as all selected (no exclusions)
    if (selectedCategories.length === 0) return [];
    return allCategories.filter(c => !selectedCategories.includes(c));
  }, [allCategories, selectedCategories]);

  return (
    <Router>
      <div className="app">
        <AutoUpdateManager />
        <Header 
          theme={theme} 
          toggleTheme={toggleTheme}
          onFilterClick={handleFilterClick}
          hasActiveFilters={hasActiveFilters}
          onTitleClick={handleTitleClick}
        />
        
        <main>
        <div className="version-info">
          v.{VERSION}
        </div>
          <Routes>
            <Route path="/" element={
              <HomePage 
                selectedRegions={selectedRegions}
                excludedCategories={excludedCategories}
                onFilterClick={handleFilterClick}
                onArticleClick={handleArticleClick}
                onRefreshReady={(refreshFn) => homePageRefreshRef.current = refreshFn}
              />
            } />
            <Route path="/artikel/:id/:slug?" element={
              <ArticleDirectLink 
                selectedRegions={selectedRegions}
                excludedCategories={excludedCategories}
                onFilterClick={handleFilterClick}
                onArticleLoad={handleArticleClick} 
              />
            } />
          </Routes>
        </main>
        
        <SlideMenu 
          isOpen={isSlideMenuOpen}
          onClose={() => setIsSlideMenuOpen(false)}
          onRegionsChange={handleRegionsChange}
          onCategoriesChange={handleCategoriesChange}
          onTotalRegionsLoaded={setTotalRegionsCount}
          onCategoriesLoaded={setAllCategories}
        />
        
        <ArticleModal 
          isOpen={isArticleModalOpen}
          onClose={handleModalClose}
          article={selectedArticle}
          isDirectVisit={isDirectVisit}
        />
      </div>
    </Router>
  );
}

export default App;
